// KARS-KI Support-Assistent – Zustand.
//
// Hält Panel-Zustand, Kontext (Gruß, Hinweise, Startfragen), den Verlauf der
// aktuellen Konversation und den Streaming-Zustand einer laufenden Antwort.
// Die Konversations-ID überlebt einen Reload (sessionStorage), damit der
// Anwender nach einem Seitenwechsel oder F5 weiterfragen kann.

import { defineStore } from 'pinia'
import { splitSuggestions } from '@/components/assistant/assistantMarkdown'
import {
  type AssistantContext,
  AssistantHttpError,
  type AssistantInsight,
  type AssistantSource,
  type AssistantStep,
  type ChatRequest,
  dismissAssistantHint,
  getAssistantContext,
  getAssistantConversation,
  rateAssistantMessage,
  streamAssistantChat,
} from '@/services/assistantApi'

export interface AssistantMessage {
  /** Server-ID; während des Streamings 0 bis `meta` eintrifft. */
  id: number
  /** Stabiler Schlüssel für v-for, unabhängig von der Server-ID. */
  key: string
  role: 'user' | 'assistant'
  content: string
  sources: AssistantSource[]
  rating: number | null
  /** Antwort wird noch gestreamt. */
  streaming: boolean
  /** Fehlertext statt Antwort. */
  error: string | null
  createdAt: number
}

const CONVERSATION_KEY = 'kars-ai-conversation'
const SEEN_KEY = 'kars-ai-insights-seen'
const MAX_MESSAGE_LENGTH = 2000

let keyCounter = 0
const nextKey = () => `m${Date.now().toString(36)}${(keyCounter++).toString(36)}`

function readStorage (key: string): string | null {
  try {
    return globalThis.sessionStorage?.getItem(key) ?? null
  } catch {
    return null
  }
}

function writeStorage (key: string, value: string | null) {
  try {
    if (value === null) {
      globalThis.sessionStorage?.removeItem(key)
    } else {
      globalThis.sessionStorage?.setItem(key, value)
    }
  } catch { /* privater Modus o. Ä. */ }
}

/** Kennung der aktuellen Hinweislage – ändert sie sich, erscheint das Badge erneut. */
export function insightsSignature (insights: AssistantInsight[]): string {
  // Frisch erzeugtes Array sortieren: toSorted() fehlt in der TS-Lib-Konfiguration (vor ES2023).
  // eslint-disable-next-line unicorn/no-array-sort
  return insights.map(i => `${i.key}:${i.severity}`).sort().join(',')
}

export const useAssistantStore = defineStore('assistant', {
  state: () => ({
    isOpen: false,
    contextLoaded: false,
    contextLoading: false,
    enabled: true,
    firstName: '',
    insights: [] as AssistantInsight[],
    starterSuggestions: [] as string[],
    knowledgeVersion: '',
    conversationId: null as number | null,
    messages: [] as AssistantMessage[],
    suggestions: [] as string[],
    streaming: false,
    step: null as AssistantStep | null,
    stepText: '',
    lastError: null as string | null,
    /** Zähler, über den Ansichten eine Frage an das Panel übergeben (Hilfe-Center). */
    pendingQuestion: null as string | null,
    seenSignature: readStorage(SEEN_KEY) ?? '',
    abortController: null as AbortController | null,
  }),

  getters: {
    hasConversation: state => state.messages.length > 0,
    /** Hinweise, die der Anwender noch nicht gesehen hat (Badge am Launcher). */
    unseenInsights (state): AssistantInsight[] {
      if (state.insights.length === 0) {
        return []
      }
      if (insightsSignature(state.insights) === state.seenSignature) {
        return []
      }
      return state.insights.filter(i => i.severity !== 'info')
    },
    highestSeverity (state): AssistantInsight['severity'] | null {
      if (state.insights.some(i => i.severity === 'warn')) {
        return 'warn'
      }
      if (state.insights.some(i => i.severity === 'hint')) {
        return 'hint'
      }
      return state.insights.length > 0 ? 'info' : null
    },
    lastAssistantMessage (state): AssistantMessage | undefined {
      for (let i = state.messages.length - 1; i >= 0; i--) {
        const m = state.messages[i]
        if (m?.role === 'assistant') {
          return m
        }
      }
      return undefined
    },
  },

  actions: {
    async ensureContext (force = false) {
      if ((this.contextLoaded && !force) || this.contextLoading) {
        return
      }
      this.contextLoading = true
      try {
        const ctx: AssistantContext = await getAssistantContext()
        this.enabled = ctx.enabled
        this.firstName = ctx.greeting?.firstName ?? ''
        this.insights = ctx.insights ?? []
        this.starterSuggestions = ctx.suggestions ?? []
        this.knowledgeVersion = ctx.knowledgeVersion ?? ''
        this.contextLoaded = true
        await this.restoreConversation(ctx.conversationId)
      } catch (error: unknown) {
        this.lastError = errorMessage(error)
      } finally {
        this.contextLoading = false
      }
    },

    /** Letzte Konversation laden: zuerst die aus dieser Browsersitzung, sonst die vom Server. */
    async restoreConversation (serverConversationId: number | null) {
      if (this.messages.length > 0) {
        return
      }
      const stored = Number(readStorage(CONVERSATION_KEY))
      const id = stored > 0 ? stored : serverConversationId
      if (!id) {
        return
      }
      try {
        const conv = await getAssistantConversation(id)
        this.conversationId = conv.id
        this.messages = conv.messages.map(m => ({
          id: m.id,
          key: nextKey(),
          role: m.role,
          content: m.content,
          sources: m.sources ?? [],
          rating: m.rating ?? null,
          streaming: false,
          error: null,
          createdAt: m.createdAt ? Date.parse(m.createdAt) : Date.now(),
        }))
        writeStorage(CONVERSATION_KEY, String(conv.id))
      } catch {
        // Konversation existiert nicht mehr – sauber neu beginnen.
        writeStorage(CONVERSATION_KEY, null)
        this.conversationId = null
      }
    },

    open () {
      this.isOpen = true
      this.markInsightsSeen()
      void this.ensureContext()
    },

    close () {
      this.isOpen = false
    },

    toggle () {
      if (this.isOpen) {
        this.close()
      } else {
        this.open()
      }
    },

    /** Vom Hilfe-Center: Panel öffnen und die Suche als Frage stellen. */
    ask (question: string) {
      this.pendingQuestion = question
      this.open()
    },

    markInsightsSeen () {
      if (this.insights.length === 0) {
        return
      }
      this.seenSignature = insightsSignature(this.insights)
      writeStorage(SEEN_KEY, this.seenSignature)
    },

    newConversation () {
      if (this.streaming) {
        this.stop()
      }
      this.conversationId = null
      this.messages = []
      this.suggestions = []
      this.lastError = null
      writeStorage(CONVERSATION_KEY, null)
    },

    async dismissInsight (key: string) {
      this.insights = this.insights.filter(i => i.key !== key)
      try {
        await dismissAssistantHint(key)
      } catch (error: unknown) {
        this.lastError = errorMessage(error)
      }
    },

    async rate (message: AssistantMessage, rating: 1 | -1) {
      if (!message.id) {
        return
      }
      const next = message.rating === rating ? 0 : rating
      this.messages = this.messages.map(m => (m.key === message.key ? { ...m, rating: next || null } : m))
      try {
        await rateAssistantMessage(message.id, next)
      } catch (error: unknown) {
        this.lastError = errorMessage(error)
      }
    },

    stop () {
      this.abortController?.abort()
    },

    async send (rawText: string, page?: ChatRequest['page'], locale?: string) {
      const text = rawText.trim().slice(0, MAX_MESSAGE_LENGTH)
      if (!text || this.streaming || !this.enabled) {
        return
      }

      this.lastError = null
      this.suggestions = []
      const userMessage: AssistantMessage = {
        id: 0, key: nextKey(), role: 'user', content: text, sources: [], rating: null, streaming: false, error: null, createdAt: Date.now(),
      }
      const assistantKey = nextKey()
      const assistantMessage: AssistantMessage = {
        id: 0, key: assistantKey, role: 'assistant', content: '', sources: [], rating: null, streaming: true, error: null, createdAt: Date.now(),
      }
      this.messages = [...this.messages, userMessage, assistantMessage]
      this.streaming = true
      this.step = 'knowledge'
      this.stepText = ''

      const controller = new AbortController()
      this.abortController = controller
      const patch = (changes: Partial<AssistantMessage>) => {
        this.messages = this.messages.map(m => (m.key === assistantKey ? { ...m, ...changes } : m))
      }
      let content = ''

      try {
        await streamAssistantChat(
          { conversationId: this.conversationId, message: text, locale, page },
          {
            onMeta: meta => {
              this.conversationId = meta.conversationId
              writeStorage(CONVERSATION_KEY, String(meta.conversationId))
              this.messages = this.messages.map(m => {
                if (m.key === userMessage.key) {
                  return { ...m, id: meta.userMessageId }
                }
                if (m.key === assistantKey) {
                  return { ...m, id: meta.assistantMessageId }
                }
                return m
              })
            },
            onStatus: status => {
              this.step = status.step
              this.stepText = status.text ?? ''
            },
            onDelta: delta => {
              if (this.step !== 'writing') {
                this.step = 'writing'
              }
              content += delta
              patch({ content })
            },
            onSources: sources => patch({ sources }),
            onDone: done => {
              const final = done.content || splitSuggestions(content).text
              patch({ content: final, streaming: false })
              this.suggestions = done.suggestions?.length ? done.suggestions : splitSuggestions(content).suggestions
            },
            onError: message => patch({ error: message, streaming: false }),
          },
          controller.signal,
        )
      } catch (error: unknown) {
        if (controller.signal.aborted) {
          const parsed = splitSuggestions(content)
          patch({ content: parsed.text, streaming: false })
        } else {
          const message = errorMessage(error)
          patch({ error: message, streaming: false })
          if (error instanceof AssistantHttpError && error.status === 503) {
            this.enabled = false
          }
        }
      } finally {
        // Falls `done` nie kam (Abbruch, Verbindungsabriss): Streaming beenden.
        this.messages = this.messages.map(m => (m.key === assistantKey && m.streaming
          ? { ...m, content: splitSuggestions(m.content).text, streaming: false }
          : m))
        this.streaming = false
        this.step = null
        this.stepText = ''
        this.abortController = null
      }
    },
  },
})

function errorMessage (err: unknown): string {
  if (err instanceof Error && err.message) {
    return err.message
  }
  if (typeof err === 'object' && err && 'message' in err && typeof (err as { message: unknown }).message === 'string') {
    return (err as { message: string }).message
  }
  return 'Unerwarteter Fehler.'
}
